import React, { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { useMutation, useQuery } from "react-query";
import { useAppContext } from "../../context/appContext";
import idApi from "../../service/IdApi";
import PageHeading from "../../components/PageHeading";
import { throttle } from "lodash";
import LazyState from "../../utils/LazyState";
import PageLoadError from "../../components/PageLoadError";
import { useLocation, useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Menu } from "primereact/menu";
import { Dialog } from "primereact/dialog";
import { Column } from "primereact/column";
import DataUtils from "../../utils/DataUtils";
import PageLoading from "../../components/PageLoading";
import GrantFacebookFriendAccess from "../../components/GrantFacebookFriendAccess";
import AddEmailFriend from "../../components/AddEmailFriend";
import { Mixpanel } from "../../utils/Mixpanel";
import AccountInfoNeeded from "../../components/AccountInfoNeeded";
import TokenService from "../../utils/TokenService";

const MyFriends = () => {
    const { displayAlert } = useAppContext();
    const location = useLocation();
    const [friends, setFriends] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [firstTimeUser, setFirstTimeUser] = useState(false);
    const [firstTimeChecking, setFirstTimeChecking] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const dataTable = useRef(null);
    const [selectedFriend, setSelectedFriend] = useState(null);
    const [selectedFriends, setSelectedFriends] = useState([]);
    const [fbFriendsAllowed, setFbFriendsAllowed] = useState(false);
    const [fbUserId, setFbUserId] = useState(null);
    const menu = useRef(null);
    const [rowActions, setRowActions] = useState([]);
    const [showGrantFacebookFriendAccess, setShowGrantFacebookFriendAccess] = useState(false);
    const [showAddEmailFriend, setShowAddEmailFriend] = useState(false);
    const [cancelInviteDialog, setCancelInviteDialog] = useState(false);
    const [removeFriendDialog, setRemoveFriendDialog] = useState(false);
    const [isCancellingInviteProcessing, setIsCancellingInviteProcessing] = useState(false);
    const [isRemovingFriendProcessing, setIsRemovingFriendProcessing] = useState(false);
    const [acceptingInviteProcessingMap, setAcceptingInviteProcessingMap] = useState(new Map());
    const [showAccountInfoNeededDialog, setShowAccountInfoNeededDialog] = useState(false);
    const [accountInfoNeededReason, setAccountInfoNeededReason] = useState("");
    const navigate = useNavigate();

    const [lazyState, setLazyState] = useState(() => {
        if (location.state && location.state.lazyState) {
            return location.state.lazyState;
        }
        return LazyState.getMyFriendsLazyState();
    });

    const { isLoading, isError, refetch } = useQuery(["MyFriends", lazyState], async () => {
        const fbInfoResult = await idApi.get("/auth/user/facebook-status");
        setFbUserId(fbInfoResult.data.fbUserId);
        if (!fbInfoResult.data.fbFriendsAllowed) {
            setFbFriendsAllowed(false);
        }
        else {
            setFbFriendsAllowed(true);
        }
        var isFiltering = false;
        var body = {};
        body.first = lazyState.first;
        if (lazyState.first !== 0) {
            isFiltering = true;
        }
        body.rows = lazyState.rows;
        body.sortField = lazyState.sortField;
        body.sortOrder = lazyState.sortOrder;
        const result = await idApi.post("/auth/user/get-friends", body);
        if (firstTimeChecking) {
            if (!isFiltering) {
                // We'll assume they are new to this feature until they've added at least 10 friends
                setFirstTimeUser(result.data.totalRecords <= 10);
            }
            setFirstTimeChecking(false);
        }
        setFriends(result.data.friends);
        setTotalRecords(result.data.totalRecords);
        setLoaded(true);
    });

    const acceptInviteMutation = useMutation((rowData) => {
        try {
            var body = {};
            body.id = rowData.id;
            body.userId1 = rowData.userId1;
            body.userId2 = rowData.userId2;
            return idApi.post("auth/user/accept-friend-invite", body);
        } catch (error) {
            throw error;
        }
    });

    const acceptInvite = throttle((rowData) => {
        if (!rowData) {
            rowData = selectedFriend;
        }
        setAcceptingInviteProcessingMap(prev => new Map(prev).set(rowData.id, true));
        acceptInviteMutation.mutate(rowData, {
            onError: (error) => {
                console.error('Failed to accept invite:', error);
                var mixpanelMessage = "Error accepting invite: " + error;
                Mixpanel.track("UI ID - Javascript error", {
                    'Javascript Error': mixpanelMessage
                });
                displayAlert("error", "Unexpected Error", "Unexpected error. Please try again later");
                setAcceptingInviteProcessingMap(prev => new Map(prev).set(rowData.id, false));
            },
            onSuccess: () => {
                displayAlert("success", "Success", "Invite accepted successfully");
                refetch();
                setSelectedFriend(null);
                setAcceptingInviteProcessingMap(prev => new Map(prev).set(rowData.id, false));
            },
        });
    }, 500, { trailing: false });

    const cancelInviteMutation = useMutation(() => {
        try {
            var body = {};
            body.id = selectedFriend.id;
            return idApi.post("auth/user/remove-friend", body);
        } catch (error) {
            throw error;
        }
    });

    const cancelInvite = throttle(() => {
        setIsCancellingInviteProcessing(true);
        cancelInviteMutation.mutate({}, {
            onError: (error) => {
                console.error('Failed to cancel invite:', error);
                var mixpanelMessage = "Error cancelling invite: " + error;
                Mixpanel.track("UI ID - Javascript error", {
                    'Javascript Error': mixpanelMessage
                });
                displayAlert("error", "Unexpected Error", "Unexpected error. Please try again later");
                setIsCancellingInviteProcessing(false);
                setSelectedFriend(null);
            },
            onSuccess: () => {
                displayAlert("success", "Success", "Invite cancelled successfully");
                refetch();
                setCancelInviteDialog(false);
                setIsCancellingInviteProcessing(false);
                setSelectedFriend(null);
            },
        });
    }, 500, { trailing: false });

    const removeFriendMutation = useMutation(() => {
        try {
            var body = {};
            body.id = selectedFriend.id;
            return idApi.post("auth/user/remove-friend", body);
        } catch (error) {
            throw error;
        }
    });

    const removeFriend = throttle(() => {
        setIsRemovingFriendProcessing(true);
        removeFriendMutation.mutate({}, {
            onError: (error) => {
                console.error('Failed to remove friend:', error);
                var mixpanelMessage = "Error removing friend: " + error;
                Mixpanel.track("UI ID - Javascript error", {
                    'Javascript Error': mixpanelMessage
                });
                displayAlert("error", "Unexpected Error", "Unexpected error. Please try again later");
                setIsRemovingFriendProcessing(false);
                setSelectedFriend(null);
            },
            onSuccess: () => {
                displayAlert("success", "Success", "Friend removed successfully");
                refetch();
                setRemoveFriendDialog(false);
                setIsRemovingFriendProcessing(false);
                setSelectedFriend(null);
            },
        });
    }, 500, { trailing: false });

    useEffect(() => {
        var revokeMenu =
        {
            label: "Cancel Invite",
            icon: "pi pi-times",
            command: () => {
                confirmCancelInvite();
            },
        }
        var removeMenu =
        {
            label: "Remove Friend",
            icon: "pi pi-trash",
            command: () => {
                confirmRemoveFriend();
            },
        }
        var acceptMenu =
        {
            label: "Accept Invite",
            icon: "pi pi-thumbs-up",
            command: () => {
                if (TokenService.accountInfoNeeded()) {
                    setAccountInfoNeededReason("accept an invite");
                    setShowAccountInfoNeededDialog(true);
                }
                else {
                    acceptInvite();
                }
            },
        }
        var _rowActions = [];
        if (DataUtils.isNotEmpty(selectedFriend?.status)) {
            if (selectedFriend.status === "p" && selectedFriend.invitedByMe) {
                _rowActions.push(revokeMenu);
            }
            else if (selectedFriend.status === "p" && !selectedFriend.invitedByMe) {
                _rowActions.push(acceptMenu);
            }
            else if (selectedFriend.status === "a") {
                _rowActions.push(removeMenu);
            }
        }
        setRowActions(_rowActions);
    }, [selectedFriend]);

    if (isError) {
        return (
            <div>
                <PageLoadError />
            </div>
        );
    }

    if (!loaded) {
        return (
            <div>
                <PageLoading />
            </div>
        );
    }

    const addFacebookFriend = throttle(() => {
        navigate('/account/add-facebook-friends');
    }, 500, { trailing: false });

    const connectWithFacebook = throttle(() => {
        setShowGrantFacebookFriendAccess(true);
    }, 500, { trailing: false });

    const addEmailFriend = throttle(() => {
        if (TokenService.accountInfoNeeded()) {
            setAccountInfoNeededReason("add a friend by email");
            setShowAccountInfoNeededDialog(true);
        }
        else {
            setShowAddEmailFriend(true);
        }
    }, 500, { trailing: false });

    const pageHeader = (
        <PageHeading pageTitle="My Friends">
            <div className="block">
                <Button className="mr-2 mb-1 sm:mb-0" icon="pi pi-envelope" label="Add Friend by Email" onClick={addEmailFriend} />
                {/* {fbUserId && fbFriendsAllowed && (
                    <Button icon="pi pi-facebook" label="Add Facebook Friend" onClick={addFacebookFriend} />
                )}
                {fbUserId && !fbFriendsAllowed && (
                    <Button icon="pi pi-facebook" label="Add Facebook Friends" onClick={connectWithFacebook} />
                )} */}
            </div>
        </PageHeading>
    );

    const onPage = (event) => {
        setLazyState(event);
        setSelectedFriends(null);
    };

    const onSort = (event) => {
        setLazyState(event);
        setSelectedFriends(null);
    };

    const confirmCancelInvite = () => {
        setIsCancellingInviteProcessing(false);
        setCancelInviteDialog(true);
    }

    const confirmRemoveFriend = () => {
        setIsRemovingFriendProcessing(false);
        setRemoveFriendDialog(true);
    }

    const showActionMenu = (e, rowData) => {
        setSelectedFriend(rowData);
        menu.current.toggle(e);
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions">
                <Button
                    icon="pi pi-bars"
                    className="p-button-rounded p-button-icon-only p-button-text text-800"
                    onClick={(e) => showActionMenu(e, rowData)}
                    aria-controls="popup_menu"
                    aria-haspopup
                    tooltip="Request Actions"
                    tooltipOptions={{ showDelay: 1000 }}
                />
            </div>
        );
    };

    const hideGrantFacebookFriendAccess = () => {
        setShowGrantFacebookFriendAccess(false);
        refetch();
    };

    const hideAddEmailFriend = () => {
        setShowAddEmailFriend(false);
    };

    const friendAdded = () => {
        refetch();
    };

    const nameBodyTemplate = (rowData) => {
        let _email = rowData.email;
        if (rowData.invitedByMe && rowData.invitedEmail) {
            _email = rowData.invitedEmail;
        }
        return (
            <div>
                {rowData.firstName} {rowData.lastName} &lt;{_email}&gt;
            </div>
        );
    };

    const statusBodyTemplate = (rowData) => {
        if (rowData.status === "a") {
            return (
                <div>
                    Active
                </div>
            );
        }
        else if (rowData.status === "p") {
            if (rowData.invitedByMe) {
                return (
                    <div>
                        Pending
                    </div>
                );
            }
            else {
                return (
                    <div>
                        <Button label="Accept Request" icon="pi pi-thumbs-up" className="p-button-text" onClick={() => {
                            if (TokenService.accountInfoNeeded()) {
                                setAccountInfoNeededReason("accept an invite");
                                setShowAccountInfoNeededDialog(true);
                            }
                            else {
                                acceptInvite(rowData);
                            }
                        }} loading={acceptingInviteProcessingMap.get(rowData.id)} />
                    </div>
                );
            }
        }
        else if (rowData.status === "d") {
            // We won't show the user that it is declined
            return (
                <div>
                    Pending
                </div>
            );
        }
    };

    const hideCancelInviteDialog = () => {
        setCancelInviteDialog(false);
    };

    const hideRemoveFriendDialog = () => {
        setRemoveFriendDialog(false);
    };

    const hideAccountInfoNeededDialog = () => {
        setShowAccountInfoNeededDialog(false);
    };

    return (
        <div>
            {pageHeader}
            <div className="layout-content">
                <div className="card">
                    {firstTimeUser && (
                        <div className="mb-3">
                            <div className="text-xl font-medium mb-2">
                                Manage Your Friends on Wallist
                            </div>
                            <div className="mb-2">
                                When it comes to hiring someone to work on your home, your business, or your life, nothing matters more than confidence in who you're choosing. Wallist is the only platform that helps you discover trusted service pros through genuine recommendations from the people who know you best, your friends and family.
                            </div>
                            <div className="mb-2">
                                By adding your friends and sharing reviews, you'll unlock a powerful network of recommendations that are personal, not paid. No more guessing. No more fake stars. Just real insights from real people you trust.
                            </div>
                            <hr />
                        </div>
                    )}
                    <Menu model={rowActions} popup ref={menu} />
                    <DataTable
                        ref={dataTable}
                        value={friends}
                        selection={selectedFriends}
                        onSelectionChange={(e) => setSelectedFriends(e.value)}
                        dataKey="id"
                        paginator
                        rows={lazyState.rows}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        className="datatable-responsive"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                        emptyMessage="No friends connected on Wallist"
                        lazy
                        first={lazyState.first}
                        totalRecords={totalRecords}
                        onPage={onPage}
                        onSort={onSort}
                        sortField={lazyState.sortField}
                        sortOrder={lazyState.sortOrder}
                        loading={isLoading || !loaded}
                        size="small"
                    >
                        <Column body={actionBodyTemplate} headerStyle={{ width: "1%", minWidth: "1rem" }} />
                        <Column body={nameBodyTemplate} field="firstName" header="Name" />
                        <Column body={statusBodyTemplate} field="status" header="Status" />
                    </DataTable>
                    <GrantFacebookFriendAccess
                        showGrantFacebookFriendAccess={showGrantFacebookFriendAccess}
                        hideGrantFacebookFriendAccess={hideGrantFacebookFriendAccess}
                    />
                    <AddEmailFriend
                        showAddEmailFriend={showAddEmailFriend}
                        hideAddEmailFriend={hideAddEmailFriend}
                        friendAdded={friendAdded}
                    />
                    <Dialog
                        visible={cancelInviteDialog}
                        style={{ width: "395px" }}
                        header="Cancel Invitation"
                        modal
                        footer={(
                            <>
                                <Button label="No" icon="pi pi-times" className="p-button-text p-button-danger" onClick={hideCancelInviteDialog} />
                                <Button label="Yes" icon="pi pi-check" onClick={cancelInvite} loading={isCancellingInviteProcessing} />
                            </>
                        )}
                        onHide={hideCancelInviteDialog}
                    >
                        <div className="flex align-items-center justify-content-center">
                            <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: "2rem" }} />
                            {selectedFriend && DataUtils.isNotEmpty(selectedFriend.firstName) && (
                                <span>
                                    Are you sure you want to cancel the invite for <b>{selectedFriend.firstName} {selectedFriend.lastName}</b>?
                                </span>
                            )}
                            {selectedFriend && DataUtils.isEmpty(selectedFriend.firstName) && (
                                <span>
                                    Are you sure you want to cancel the invite for <b>{selectedFriend.email || selectedFriend.invitedEmail}</b>?
                                </span>
                            )}
                        </div>
                    </Dialog>
                    <Dialog
                        visible={removeFriendDialog}
                        style={{ width: "350px" }}
                        header="Remove Friend"
                        modal
                        footer={(
                            <>
                                <Button label="No" icon="pi pi-times" className="p-button-text p-button-danger" onClick={hideRemoveFriendDialog} />
                                <Button label="Yes" icon="pi pi-check" onClick={removeFriend} loading={isRemovingFriendProcessing} />
                            </>
                        )}
                        onHide={hideRemoveFriendDialog}
                    >
                        <div className="flex align-items-center justify-content-center">
                            <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: "2rem" }} />
                            {selectedFriend && DataUtils.isNotEmpty(selectedFriend.firstName) && (
                                <span>
                                    Are you sure you want to remove <b>{selectedFriend.firstName} {selectedFriend.lastName}</b> as a friend?
                                </span>
                            )}
                            {selectedFriend && DataUtils.isEmpty(selectedFriend.firstName) && (
                                <span>
                                    Are you sure you want to remove <b>{selectedFriend.email}</b> as a friend?
                                </span>
                            )}
                        </div>
                    </Dialog>
                    <AccountInfoNeeded
                        showAccountInfoNeededDialog={showAccountInfoNeededDialog}
                        hideAccountInfoNeededDialog={hideAccountInfoNeededDialog}
                        accountInfoNeededReason={accountInfoNeededReason}
                    />
                </div>
            </div>
        </div>
    );
};

export default MyFriends;
